import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button } from "@mui/material";
import { useSnackbar } from "notistack";

import { HistoryContext, useHistory } from "fond/history";
import { getHotkeyLabel, useHotkeys } from "fond/hooks/useHotkeys";
import { SplitPolygon } from "fond/svg_icons";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";
import { useGeometryTypeCheck } from "./useGeometryTypeCheck";
import { useToolEnabled } from "./useToolEnabled";

const PASSING_MODES = ["split_polygon_passing_draw_line_string", "split_line_string_passing_draw_line_string"];

const SplitButton: React.FC<IButtonProps> = ({ icon, mode, title = "Split features", ...rest }) => {
  const { drawControl, map } = useContext(MapContext);
  const [isDisabled, setIsDisabled] = useState(true);
  const isPolygonLayer = useGeometryTypeCheck("Polygon");
  const isLineStringLayer = useGeometryTypeCheck("LineString");
  const isToolEnabled = useToolEnabled("split");
  const isSplittableLayer = isPolygonLayer || isLineStringLayer;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { undo } = useHistory(HistoryContext);

  /**
   * Monitor when the selectionchange event occurs so that we can disable
   * this feature based on if features are selected.
   */
  const handleOnSelect = useCallback(() => {
    const selectedFeatures = drawControl.current?.getSelected().features || [];
    setIsDisabled(selectedFeatures.length === 0);
  }, [drawControl]);

  const handleOnSplit = useCallback(
    (e: { action?: string }) => {
      if (e.action === "split_feature") {
        const snackbarKey = enqueueSnackbar("Features split successfully", {
          action: (
            <Button
              color="inherit"
              onClick={() => {
                undo();
                closeSnackbar(snackbarKey);
              }}
            >
              Undo
            </Button>
          ),
        });
      }
    },
    [enqueueSnackbar, closeSnackbar, undo]
  );

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelect);
    map?.on("draw.update", handleOnSplit);

    return () => {
      map?.off("draw.selectionchange", handleOnSelect);
      map?.off("draw.update", handleOnSplit);
    };
  }, [map, handleOnSelect, handleOnSplit]);

  const handleOnChange = useCallback(() => {
    if (PASSING_MODES.includes(mode || "")) {
      // Already in a split passing mode — cancel
      drawControl.current?.changeMode("simple_select");
      map?.fire("draw.modechange", { type: "draw.modechange", mode: "simple_select" });
    } else {
      // Determine which split mode to enter based on selected features
      const selectedFeatures = drawControl.current?.getSelected().features || [];
      const hasPolygons = selectedFeatures.some((f) => f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon");
      const hasLineStrings = selectedFeatures.some((f) => f.geometry?.type === "LineString");

      if (hasPolygons) {
        drawControl.current?.changeMode("split_polygon");
      } else if (hasLineStrings) {
        drawControl.current?.changeMode("split_line_string");
      }
    }
  }, [drawControl, map, mode]);

  const selected = PASSING_MODES.includes(mode || "");
  const isSplitDisabled = !isSplittableLayer || !isToolEnabled || (!selected && isDisabled);

  useHotkeys({
    action: "SPLIT",
    callback: (event) => {
      event.preventDefault();
      handleOnChange();
    },
    enabled: !isSplitDisabled,
  });

  // Cancel the split tool on Escape
  useHotkeys({
    action: "ESCAPE",
    callback: (event) => {
      event.preventDefault();
      handleOnChange();
    },
    enabled: selected,
  });

  const tooltipTitle = `${title} (${getHotkeyLabel("SPLIT")})`;

  return (
    <ToolbarToggleButton
      title={title}
      {...rest}
      tooltip={tooltipTitle}
      value="split"
      selected={selected}
      onChange={handleOnChange}
      disabled={isSplitDisabled}
    >
      {icon || <SplitPolygon />}
    </ToolbarToggleButton>
  );
};

export default SplitButton;
